import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { StepIndicator } from '../components/StepIndicator';
import { FocusHeader } from '../components/FocusHeader';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { CreditCard as Edit2, Send, ArrowLeft, Image as ImageIcon, Globe } from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';

export function BlogPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const sessionId = sessionIdFromLocation(location);
  const { session } = useSession();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [blogData, setBlogData] = useState<any>(null);
  const [error, setError] = useState('');
  const [dashboardSession, setDashboardSession] = useState<any>(null);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else if (session?.id) {
      setLoading(true);
      loadBlogData(session.id);
    } else {
      navigate(WIZARD_EXIT_ROUTE, { replace: true });
    }
  }, [session?.id, sessionId, location.key]);

  const loadDashboardSession = async (sessionId: string) => {
    try {
      setLoading(true);

      const { data: sessionData } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionData) {
        setDashboardSession(sessionData);
        await loadBlogData(sessionId);
      } else {
        navigate(WIZARD_EXIT_ROUTE, { replace: true });
      }
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setError('Kon sessie niet laden');
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const { handleStepClick } = useWizardNavigation(activeSession);

  const goBack = () => {
    const back = backUrlFor(WIZARD_PATHS.preview, sessionId ?? activeSession?.id);
    if (back) navigate(back);
  };

  const loadBlogData = async (sessionId: string) => {
    try {
      console.log('Loading blog data for session:', sessionId);

      const { data, error } = await supabase
        .from('sessions')
        .select('*, blog_ideas!sessions_approved_idea_id_fkey(*)')
        .eq('id', sessionId)
        .maybeSingle();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data) {
        console.error('No session data found');
        setError('Sessie niet gevonden');
        return;
      }

      console.log('BlogPreview - loaded full data:', data);
      console.log('BlogPreview - selected values:', {
        selected_title: data.selected_title,
        selected_intro: data.selected_intro,
        selected_image_url: data.selected_image_url,
        article_content: data.article_content,
        idea: data.blog_ideas,
      });

      setBlogData(data);
    } catch (err: any) {
      console.error('Error loading blog data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    // Validate all parts are selected
    if (!blogData?.selected_title || !blogData?.selected_intro || !blogData?.selected_image_url) {
      setError('Niet alle onderdelen zijn geselecteerd. Zorg dat je een titel, intro en afbeelding hebt gekozen.');
      return;
    }

    if (!activeSession?.id) {
      setError('Sessie niet gevonden. Ververs de pagina en probeer het opnieuw.');
      return;
    }

    console.log('BlogPreview - Starting publish flow for session:', activeSession.id);
    setSending(true);
    setError('');

    try {
      // Navigate to success page with session data
      navigate(wizardUrl(WIZARD_PATHS.success, activeSession.id), {
        state: {
          sessionId: activeSession.id,
        },
      });
    } catch (err: any) {
      console.error('Error navigating to success:', err);
      setError('Er ging iets mis bij het openen van de publicatiepagina. Probeer het opnieuw.');
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingSpinner text="Blog preview laden..." />;
  }

  if (error && !blogData) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center p-4">
        <div className="card max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={goBack} className="btn-secondary w-full">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Terug
          </button>
        </div>
      </div>
    );
  }

  const sessionForSteps = activeSession || blogData;
  const steps = getWizardSteps(sessionForSteps, 7);
  const isPublished = (blogData?.state ?? activeSession?.state) === 'completed';

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Bekijk preview"
        onBack={goBack}
        onClose={() => navigate(WIZARD_EXIT_ROUTE)}
        hideSaveStatus
      />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#231F20] mb-2">Preview van je blogartikel</h1>
          <p className="text-gray-600 mb-6">
            Bekijk hieronder hoe je blogartikel eruit komt te zien. Klik op een onderdeel om het aan te passen.
          </p>

          {/* Action Button */}
          {isPublished ? (
            <div className="card border border-green-200 bg-green-50 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <p className="text-sm text-gray-700">Deze blog is al gepubliceerd. Wijzigingen doe je in de blog-editor onder Mijn Blogs.</p>
              <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary whitespace-nowrap">Naar Mijn Blogs</button>
            </div>
          ) : (
          <button
            onClick={handleSend}
            disabled={sending || !blogData?.selected_title || !blogData?.selected_intro || !blogData?.selected_image_url}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Publiceren...
              </>
            ) : (
              <>
                <Globe className="w-5 h-5" />
                Publiceren
              </>
            )}
          </button>
          )}

          {/* Error Message */}
          {error && (
            <div className="card bg-red-50 border-red-200 mt-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}
        </div>

      {/* Blog Preview Card */}
      <div className="card mb-8">
        {/* Title Section */}
        <div className="border-b border-gray-200 pb-6 mb-6 relative group">
          {!isPublished && (
          <button
            onClick={() =>
              navigate(
                wizardUrl(
                  blogData?.titles && blogData.titles.length > 0 ? WIZARD_PATHS.selectTitle : WIZARD_PATHS.titleGenerate,
                  activeSession?.id,
                ),
              )
            }
            className="absolute top-0 right-0 btn-secondary flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Titel aanpassen"
          >
            <Edit2 className="w-4 h-4" />
            {blogData?.selected_title ? 'Wijzig' : 'Kies'}
          </button>
          )}
          {blogData?.selected_title ? (
            <h2 className="text-4xl font-bold text-[#231F20] mb-2 pr-24">
              {blogData.selected_title}
            </h2>
          ) : (
            <div className="pr-24">
              <p className="text-xl text-gray-400 italic mb-2">Nog geen titel gekozen</p>
              <p className="text-sm text-gray-500">Klik op "Kies" om een titel te selecteren</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-2">Titel</p>
        </div>

        {/* Intro Section */}
        <div className="border-b border-gray-200 pb-6 mb-6 relative group">
          {!isPublished && (
          <button
            onClick={() =>
              navigate(
                wizardUrl(
                  blogData?.intros && blogData.intros.length > 0 ? WIZARD_PATHS.selectIntro : WIZARD_PATHS.introGenerate,
                  activeSession?.id,
                ),
              )
            }
            className="absolute top-0 right-0 btn-secondary flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Intro aanpassen"
          >
            <Edit2 className="w-4 h-4" />
            {blogData?.selected_intro ? 'Wijzig' : 'Kies'}
          </button>
          )}
          {blogData?.selected_intro ? (
            <p className="text-xl text-gray-700 leading-relaxed mb-2 pr-24">
              {blogData.selected_intro}
            </p>
          ) : (
            <div className="pr-24">
              <p className="text-xl text-gray-400 italic mb-2">Nog geen introductie gekozen</p>
              <p className="text-sm text-gray-500">Klik op "Kies" om een intro te selecteren</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-2">Introductie</p>
        </div>

        {/* Image Section */}
        <div className="border-b border-gray-200 pb-6 mb-6 relative group">
          {!isPublished && (
          <button
            onClick={() =>
              navigate(
                wizardUrl(
                  blogData?.images && blogData.images.length > 0 ? WIZARD_PATHS.selectImage : WIZARD_PATHS.imageGenerate,
                  activeSession?.id,
                ),
              )
            }
            className="absolute top-4 right-4 btn-secondary flex items-center gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
            title="Afbeelding aanpassen"
          >
            <ImageIcon className="w-4 h-4" />
            {blogData?.selected_image_url ? 'Wijzig' : 'Kies'}
          </button>
          )}
          {blogData?.selected_image_url ? (
            <div className="relative rounded-lg overflow-hidden bg-gray-100 mb-2">
              <img
                src={`${blogData.selected_image_url}?t=${Date.now()}`}
                alt="Blog afbeelding"
                className="w-full h-auto object-cover"
                style={{ maxHeight: '500px' }}
              />
            </div>
          ) : (
            <div className="relative rounded-lg overflow-hidden bg-gray-100 mb-2 p-12 text-center">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-xl text-gray-400 italic mb-2">Nog geen afbeelding gekozen</p>
              <p className="text-sm text-gray-500">Klik op "Kies" om een afbeelding te selecteren</p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-2">Hoofdafbeelding</p>
        </div>

        {/* Article Content Section */}
        <div className="relative group">
          {!isPublished && (
          <button
            onClick={() => navigate(wizardUrl(WIZARD_PATHS.articleEdit, activeSession?.id))}
            className="absolute top-0 right-0 btn-secondary flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Artikel bewerken"
          >
            <Edit2 className="w-4 h-4" />
            Wijzig
          </button>
          )}
          <div className="prose prose-lg max-w-none pr-24">
            <div
              className="blog-content text-gray-800"
              dangerouslySetInnerHTML={{ __html: blogData?.article_content || '' }}
            />
          </div>
          <p className="text-sm text-gray-500 mt-4">Artikel inhoud</p>
        </div>
      </div>
      </div>
    </div>
  );
}
